import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CloudUpload, X, Video, CheckCircle2 } from "lucide-react";

import Button from "../../../../components/buttons/Button";
import TextField from "../../../../components/input/textfield/TextField";
import Select from "../../../../components/input/select/Select";

import { generateUniqueId } from "../../../../utils/helpers/generator/generateUniqueId";
import { s3Uploader } from "../../../../utils/helpers/s3Uploader/s3Uploader";

import { useCreatorPops } from "../hooks/useCreatorPops";
import { useCreatorPopUpload } from "../hooks/useCreatorPopUpload";

import "./CreatorPopUploadForm.css";

const VISIBILITY_OPTIONS = [
  { label: "Public", value: "public" },
  { label: "Unlisted", value: "unlisted" },
  { label: "Group", value: "group" },
  { label: "Private", value: "private" },
];

const MAXIMUM_SIZE_MB = 100;
const MAXIMUM_SIZE_BYTES = MAXIMUM_SIZE_MB * 1024 * 1024;
const MAXIMUM_DURATION = 60;

const getErrorMessage = (error, fallback) =>
  typeof error === "string"
    ? error
    : error?.response?.data?.message || error?.message || fallback;

const formatDuration = (seconds) => {
  const total = Math.floor(Number(seconds) || 0);

  return `${Math.floor(total / 60)}m ${String(total % 60).padStart(2, "0")}s`;
};

const formatFileSize = (bytes) =>
  `${((Number(bytes) || 0) / (1024 * 1024)).toFixed(2)} MB`;

const getVisibilityNote = (visibility) => {
  switch (visibility) {
    case "group":
      return "After uploading, edit this Pop to select which groups can access it.";

    case "private":
      return "Only you can access this Pop.";

    case "unlisted":
      return "Only people with access to this Pop can view it.";

    default:
      return "This Pop will be publicly available.";
  }
};

export default function CreatorPopUploadForm({
  username,
  onClose,
  closeModal,
  onUploadingChange,
  onSuccess,
}) {
  const {
    adding,
    error: creatorError,
    addCreatorPop,
  } = useCreatorPops(username);

  const { loadingUploadUrls, uploadError, getCreatorPopUploadUrls } =
    useCreatorPopUpload();

  const [videoFile, setVideoFile] = useState(null);

  const [formData, setFormData] = useState(() => ({
    popTag: generateUniqueId(6),
    title: "",
    tags: "",
    visibility: "private",
  }));

  const [isUploading, setIsUploading] = useState(false);
  const [processingFile, setProcessingFile] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [localError, setLocalError] = useState("");

  const fileInputRef = useRef(null);
  const fileOperationRef = useRef(false);
  const fileSelectionRef = useRef(0);
  const uploadingRef = useRef(false);
  const uploadCompletedRef = useRef(false);
  const mountedRef = useRef(true);
  const onUploadingChangeRef = useRef(onUploadingChange);

  const loading = isUploading || adding || loadingUploadUrls;
  const busy = loading || processingFile;

  const visibility = formData.visibility || "private";

  const rawError = localError || uploadError || creatorError;

  const error = rawError
    ? getErrorMessage(rawError, "Unable to upload Pop.")
    : "";

  const canSubmit =
    Boolean(username) &&
    Boolean(videoFile) &&
    Boolean(formData.title.trim()) &&
    formData.title.trim().length <= 255 &&
    VISIBILITY_OPTIONS.some((option) => option.value === visibility) &&
    !busy;

  useEffect(() => {
    onUploadingChangeRef.current = onUploadingChange;
  }, [onUploadingChange]);

  useEffect(() => {
    onUploadingChangeRef.current?.(busy);
  }, [busy]);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      fileSelectionRef.current += 1;
    };
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!uploadingRef.current || uploadCompletedRef.current) {
        return;
      }

      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (videoFile?.url) {
        URL.revokeObjectURL(videoFile.url);
      }
    };
  }, [videoFile?.url]);

  const handleClose = () => {
    if (busy || uploadingRef.current) return;

    if (typeof onClose === "function") {
      onClose();
    } else {
      closeModal?.(false);
    }
  };

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape" && !busy) {
        event.preventDefault();

        if (typeof onClose === "function") {
          onClose();
        } else {
          closeModal?.(false);
        }
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [busy, onClose, closeModal]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));

    setLocalError("");
  };

  const clearSelectedVideo = () => {
    if (busy || uploadingRef.current) return;

    fileSelectionRef.current += 1;

    setVideoFile(null);
    setProgress(0);
    setLocalError("");
    setIsDragging(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const processFile = async (file) => {
    if (!file || busy || fileOperationRef.current) return;

    setLocalError("");

    if (
      file.type !== "video/mp4" &&
      !file.name.toLowerCase().endsWith(".mp4")
    ) {
      setLocalError("Only MP4 files are allowed.");
      return;
    }

    if (file.size <= 0) {
      setLocalError("The selected video file is empty.");
      return;
    }

    if (file.size > MAXIMUM_SIZE_BYTES) {
      setLocalError(`File size exceeds the ${MAXIMUM_SIZE_MB} MB limit.`);
      return;
    }

    fileOperationRef.current = true;

    const selectionId = ++fileSelectionRef.current;

    setProcessingFile(true);

    const metadataUrl = URL.createObjectURL(file);
    const metadataVideo = document.createElement("video");

    metadataVideo.preload = "metadata";
    metadataVideo.muted = true;
    metadataVideo.playsInline = true;

    try {
      const duration = await new Promise((resolve, reject) => {
        metadataVideo.onloadedmetadata = () => {
          resolve(Number(metadataVideo.duration));
        };

        metadataVideo.onerror = () => {
          reject(new Error("Unable to read video metadata."));
        };

        metadataVideo.src = metadataUrl;
      });

      if (!Number.isFinite(duration) || duration <= 0) {
        throw new Error("Invalid video duration.");
      }

      if (duration > MAXIMUM_DURATION) {
        throw new Error(
          `Pops must be a maximum of ${MAXIMUM_DURATION} seconds long.`,
        );
      }

      if (!mountedRef.current || selectionId !== fileSelectionRef.current) {
        return;
      }

      const previewUrl = URL.createObjectURL(file);

      setVideoFile({
        file,
        filename: `${generateUniqueId()}.mp4`,
        type: "video/mp4",
        size: file.size,
        duration,
        url: previewUrl,
      });

      setFormData((previous) => ({
        ...previous,
        title:
          previous.title.trim() ||
          file.name.replace(/\.[^.]+$/, "").slice(0, 255),
      }));

      setProgress(0);
      uploadCompletedRef.current = false;
    } catch (processError) {
      if (mountedRef.current && selectionId === fileSelectionRef.current) {
        console.error("Pop video processing failed:", processError);

        setLocalError(
          getErrorMessage(
            processError,
            "Unable to process the selected video.",
          ),
        );
      }
    } finally {
      metadataVideo.removeAttribute("src");
      metadataVideo.load();

      URL.revokeObjectURL(metadataUrl);

      fileOperationRef.current = false;

      if (mountedRef.current && selectionId === fileSelectionRef.current) {
        setProcessingFile(false);
      }
    }
  };

  const captureThumbnail = async () => {
    if (!videoFile?.file) {
      throw new Error("No video selected.");
    }

    const video = document.createElement("video");
    const thumbnailUrl = URL.createObjectURL(videoFile.file);

    video.preload = "auto";
    video.muted = true;
    video.playsInline = true;

    try {
      await new Promise((resolve, reject) => {
        video.onloadedmetadata = resolve;

        video.onerror = () => {
          reject(new Error("Unable to load video for thumbnail generation."));
        };

        video.src = thumbnailUrl;
      });

      const thumbnailTime = Math.min(2, Math.max(0.1, video.duration / 2));

      await new Promise((resolve, reject) => {
        video.onseeked = resolve;

        video.onerror = () => {
          reject(new Error("Unable to capture video thumbnail."));
        };

        video.currentTime = thumbnailTime;
      });

      if (!video.videoWidth || !video.videoHeight) {
        throw new Error("Video frame is not available.");
      }

      const canvas = document.createElement("canvas");

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const context = canvas.getContext("2d");

      if (!context) {
        throw new Error("Thumbnail canvas is unavailable.");
      }

      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (result) => {
            if (!result) {
              reject(new Error("Thumbnail generation failed."));
              return;
            }

            resolve(result);
          },
          "image/jpeg",
          0.8,
        );
      });

      return new File([blob], `${generateUniqueId()}.jpg`, {
        type: "image/jpeg",
      });
    } finally {
      video.removeAttribute("src");
      video.load();

      URL.revokeObjectURL(thumbnailUrl);
    }
  };

  const validateUploadReady = () => {
    if (!username) {
      throw new Error("Creator username is required.");
    }

    const title = formData.title.trim();

    if (!title) {
      throw new Error("Please enter a title.");
    }

    if (title.length > 255) {
      throw new Error("Pop title cannot exceed 255 characters.");
    }

    if (!VISIBILITY_OPTIONS.some((option) => option.value === visibility)) {
      throw new Error("Please select a valid visibility option.");
    }

    if (
      !videoFile?.file ||
      !videoFile?.filename ||
      !Number.isFinite(videoFile.duration) ||
      videoFile.duration <= 0 ||
      videoFile.duration > MAXIMUM_DURATION
    ) {
      throw new Error("Please select a valid video.");
    }

    return true;
  };

  const startUpload = async () => {
    if (!videoFile || busy || uploadingRef.current) return;

    uploadingRef.current = true;
    uploadCompletedRef.current = false;

    setIsUploading(true);
    setProgress(0);
    setLocalError("");

    try {
      validateUploadReady();

      setProgress(2);

      const thumbnailFile = await captureThumbnail();

      if (mountedRef.current) {
        setProgress(5);
      }

      const uploadUrls = await getCreatorPopUploadUrls();

      if (!uploadUrls) {
        throw new Error("Unable to obtain S3 upload information.");
      }

      const { videoS3Data, thumbnailS3Data } = uploadUrls;

      if (!videoS3Data || !thumbnailS3Data) {
        throw new Error("Invalid S3 upload information.");
      }

      await s3Uploader(
        videoS3Data,
        {
          file: videoFile.file,
          filename: videoFile.filename,
        },
        0,
        (_index, percentage) => {
          if (!mountedRef.current) return;

          const percent = Number(percentage);

          if (!Number.isFinite(percent)) return;

          setProgress(Math.min(85, Math.max(5, Math.round(5 + percent * 0.8))));
        },
      );

      if (mountedRef.current) {
        setProgress(85);
      }

      await s3Uploader(thumbnailS3Data, {
        file: thumbnailFile,
        filename: thumbnailFile.name,
      });

      if (mountedRef.current) {
        setProgress(95);
      }

      const payload = {
        popTag: formData.popTag,
        title: formData.title.trim(),
        tags: formData.tags.trim(),
        visibility,
        thumbnailKey: thumbnailFile.name,
        videoKey: videoFile.filename,
        videoSize: videoFile.size,
        videoType: videoFile.type,
        videoDuration: videoFile.duration,
      };

      const result = await addCreatorPop(payload);

      if (!result?.success) {
        throw new Error(result?.message || "Failed to publish Pop.");
      }

      uploadCompletedRef.current = true;

      if (mountedRef.current) {
        setProgress(100);
      }

      if (typeof onSuccess === "function") {
        await onSuccess(result);
      } else if (typeof onClose === "function") {
        onClose(true);
      } else {
        closeModal?.(false);
      }
    } catch (uploadFailure) {
      console.error("Pop upload failed:", uploadFailure);

      if (mountedRef.current && !uploadCompletedRef.current) {
        setLocalError(
          getErrorMessage(uploadFailure, "An error occurred during upload."),
        );

        setProgress(0);
      }
    } finally {
      uploadingRef.current = false;

      if (mountedRef.current) {
        setIsUploading(false);
      }

      onUploadingChangeRef.current?.(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    void startUpload();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);

    if (!busy) {
      void processFile(event.dataTransfer?.files?.[0]);
    }
  };

  const handleBrowse = () => {
    if (!busy) {
      fileInputRef.current?.click();
    }
  };

  return createPortal(
    <form
      className="creator-pop-upload-form"
      onSubmit={handleSubmit}
      aria-label="Upload Pop"
    >
      <header className="creator-pop-upload-form__header">
        <div className="creator-pop-upload-form__header-info">
          <h2>Upload Pop</h2>
          <p>Upload and share your short videos on Katch.</p>
        </div>

        <button
          type="button"
          className="creator-pop-upload-form__close"
          onClick={handleClose}
          disabled={busy}
          aria-label="Close Pop upload"
        >
          <X size={22} />
        </button>
      </header>

      <div className="creator-pop-upload-form__workspace">
        <main className="creator-pop-upload-form__main">
          <div className="creator-pop-upload-form__toolbar">
            <div>
              <h3>Pop Preview</h3>
              <p>
                MP4 only · Maximum {MAXIMUM_SIZE_MB} MB · Up to{" "}
                {MAXIMUM_DURATION} seconds
              </p>
            </div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".mp4,video/mp4"
            hidden
            disabled={busy}
            onChange={(event) => {
              void processFile(event.target.files?.[0]);
            }}
          />

          {!videoFile ? (
            <div
              className={`creator-pop-upload-form__dropzone ${
                isDragging ? "creator-pop-upload-form__dropzone--active" : ""
              }`}
              role="button"
              tabIndex={busy ? -1 : 0}
              aria-label="Choose an MP4 video"
              aria-disabled={busy}
              onDragOver={(event) => {
                event.preventDefault();

                if (!busy) {
                  setIsDragging(true);
                }
              }}
              onDragLeave={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) {
                  setIsDragging(false);
                }
              }}
              onDrop={handleDrop}
              onClick={handleBrowse}
              onKeyDown={(event) => {
                if ((event.key === "Enter" || event.key === " ") && !busy) {
                  event.preventDefault();
                  handleBrowse();
                }
              }}
            >
              <div className="creator-pop-upload-form__upload-icon">
                <CloudUpload size={32} strokeWidth={1.5} />
              </div>

              <strong>
                {processingFile ? "Processing video..." : "Choose an MP4 video"}
              </strong>

              <span>
                {processingFile
                  ? "Reading video metadata"
                  : "Click to browse or drag and drop"}
              </span>

              <small>
                Maximum {MAXIMUM_SIZE_MB} MB · {MAXIMUM_DURATION} seconds
              </small>
            </div>
          ) : (
            <div className="creator-pop-upload-form__preview">
              <div className="creator-pop-upload-form__player">
                <video
                  key={videoFile.url}
                  src={videoFile.url}
                  controls
                  playsInline
                  preload="metadata"
                  controlsList="nodownload"
                  disablePictureInPicture
                />

                {!busy && (
                  <button
                    type="button"
                    className="creator-pop-upload-form__remove"
                    onClick={clearSelectedVideo}
                    aria-label="Remove selected video"
                  >
                    <X size={18} />
                  </button>
                )}
              </div>

              <div className="creator-pop-upload-form__file">
                <span className="creator-pop-upload-form__file-icon">
                  <Video size={20} />
                </span>

                <div className="creator-pop-upload-form__file-info">
                  <strong>{videoFile.file.name}</strong>
                  <span>
                    {formatDuration(videoFile.duration)} ·{" "}
                    {formatFileSize(videoFile.size)}
                  </span>
                </div>

                <CheckCircle2
                  size={20}
                  className="creator-pop-upload-form__file-success"
                />
              </div>
            </div>
          )}

          {loading && (
            <div
              className="creator-pop-upload-form__progress"
              role="status"
              aria-live="polite"
            >
              <div className="creator-pop-upload-form__progress-header">
                <span>Uploading Pop</span>
                <strong>{progress}%</strong>
              </div>

              <div
                className="creator-pop-upload-form__progress-track"
                role="progressbar"
                aria-label="Pop upload progress"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={progress}
              >
                <div
                  className="creator-pop-upload-form__progress-fill"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}
        </main>

        <aside className="creator-pop-upload-form__sidebar">
          <div className="creator-pop-upload-form__sidebar-header">
            <h3>Pop Details</h3>
            <p>Provide information about your Pop.</p>
          </div>

          <div className="creator-pop-upload-form__fields">
            <TextField
              name="title"
              label="Title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Enter Pop title"
              maxLength={255}
              required
              disabled={busy}
            />

            <TextField
              name="tags"
              label="Tags"
              value={formData.tags}
              onChange={handleInputChange}
              placeholder="Enter tags"
              disabled={busy}
            />

            <Select
              name="visibility"
              label="Visibility"
              value={visibility}
              onChange={handleInputChange}
              items={VISIBILITY_OPTIONS}
              required
              disabled={busy}
            />

            <p className="creator-pop-upload-form__visibility-note">
              {getVisibilityNote(visibility)}
            </p>
          </div>

          {videoFile && (
            <div className="creator-pop-upload-form__summary">
              <h4>Pop Summary</h4>

              <div>
                <span>Duration</span>
                <strong>{formatDuration(videoFile.duration)}</strong>
              </div>

              <div>
                <span>File size</span>
                <strong>{formatFileSize(videoFile.size)}</strong>
              </div>

              <div>
                <span>Visibility</span>
                <strong>{visibility}</strong>
              </div>
            </div>
          )}
        </aside>
      </div>

      <footer className="creator-pop-upload-form__footer">
        <div className="creator-pop-upload-form__footer-status">
          {error ? (
            <span className="creator-pop-upload-form__error" role="alert">
              {error}
            </span>
          ) : loading ? (
            <span>Uploading Pop · {progress}%</span>
          ) : processingFile ? (
            <span>Processing video...</span>
          ) : null}
        </div>

        <div className="creator-pop-upload-form__actions">
          <Button
            type="button"
            value="Cancel"
            variant="primary-outlined"
            onClick={handleClose}
            disabled={busy}
          />

          <Button
            type="submit"
            value={
              loading
                ? `Uploading ${progress}%`
                : processingFile
                  ? "Processing..."
                  : "Publish Pop"
            }
            variant="primary"
            disabled={!canSubmit}
          />
        </div>
      </footer>
    </form>,
    document.body,
  );
}
